import { ginzaxiaomaApiRequest } from 'apis';
import type { AxiosProgressEvent } from 'axios';

type UploadFileResponse = {
  url: string;
  key: string;
  mimetype: string;
  originalname: string;
  size: number;
};

export type UploadProgress = {
  loaded: number;
  total?: number;
};

const commonApi = {
  photoRoomRemoveBackground(dto: { url: string; addShadow: boolean }) {
    return ginzaxiaomaApiRequest.post<Promise<string>>(
      '/admin/common/photo-room/remove-background-url',
      dto,
    );
  },

  uploadFile(
    file: File,
    isPrivate: boolean = false,
    options?: {
      onProgress?: (progress: UploadProgress) => void;
      compress?: boolean;
      /** 压缩质量，1-100；仅 compress 时生效 */
      quality?: number;
      /** 长边上限；0 表示不改尺寸。仅 compress 时生效 */
      maxSize?: number;
      productId?: number;
      recycleOrderId?: number | string;
    },
  ) {
    const formData = new FormData();
    formData.append('file', file);
    if (isPrivate) {
      formData.append('isPrivate', 'true');
    }
    if (options?.compress) {
      formData.append('compress', 'true');
    }
    if (options?.quality !== undefined) {
      formData.append('quality', String(options.quality));
    }
    if (options?.maxSize !== undefined) {
      formData.append('maxSize', String(options.maxSize));
    }
    if (options?.productId != null) {
      formData.append('productId', String(options.productId));
    }
    if (options?.recycleOrderId != null) {
      formData.append('recycleOrderId', String(options.recycleOrderId));
    }

    return ginzaxiaomaApiRequest.post<UploadFileResponse>(
      '/admin/common/upload-file',
      formData,
      {
        onUploadProgress: (e: AxiosProgressEvent) => {
          options?.onProgress?.({
            loaded: e.loaded ?? 0,
            total: e.total ?? undefined,
          });
        },
      },
    );
  },

  // 商品图片上传：服务端会做压缩/转 webp/去元数据等处理
  uploadProductImage(
    file: File,
    options?: {
      onProgress?: (progress: UploadProgress) => void;
      maxSize?: number; // 最大尺寸，默认 2048
      quality?: number; // 压缩质量 (1-100)，默认 85
      productId?: number;
    },
  ) {
    const formData = new FormData();
    formData.append('file', file);

    // 添加可选参数
    if (options?.maxSize !== undefined) {
      formData.append('maxSize', String(options.maxSize));
    }
    if (options?.quality !== undefined) {
      formData.append('quality', String(options.quality));
    }
    if (options?.productId != null) {
      formData.append('productId', String(options.productId));
    }

    return ginzaxiaomaApiRequest.post<UploadFileResponse>(
      '/admin/common/upload-product-image',
      formData,
      {
        onUploadProgress: (e: AxiosProgressEvent) => {
          options?.onProgress?.({
            loaded: e.loaded ?? 0,
            total: e.total ?? undefined,
          });
        },
      },
    );
  },

  uploadFakeBagImage(file: File, isPrivate: boolean = false) {
    const formData = new FormData();
    formData.append('file', file);
    if (isPrivate) {
      formData.append('isPrivate', 'true');
    }

    return ginzaxiaomaApiRequest.post<UploadFileResponse>(
      '/admin/common/upload-fake-bag-image',
      formData,
    );
  },

  getSignedFileUrl(key: string) {
    return ginzaxiaomaApiRequest.get<{
      url: string;
    }>(`/admin/common/get-signed-file-url?key=${key}`);
  },
  recycleConsignmentDraftData() {
    return ginzaxiaomaApiRequest.get<
      {
        title: string;
        time: number;
        data: string;
        shopId?: number;
      }[]
    >('/admin/common/recycle-consignment-draft-data');
  },
  setRecycleConsignmentDraftData(data: any) {
    return ginzaxiaomaApiRequest.post(
      '/admin/common/recycle-consignment-draft-data',
      data,
    );
  },
  getNgeniusPaymentTransaction(transactionId: string) {
    return ginzaxiaomaApiRequest.get(
      `/admin/common/ngenius-payment/transactions/${transactionId}`,
    );
  },
  manualConfirmNgeniusPayment(transactionId: string) {
    return ginzaxiaomaApiRequest.post(
      `/admin/common/ngenius-payment/manual-confirm/${transactionId}`,
    );
  },
};

export default commonApi;
